import {
  useState,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  createContext,
  useContext,
  useMemo,
} from "react";
import {
  AI_processor,
  self_disable,
  secure_processor,
  io,
  antiTamper,
  galvanic,
  router,
  offlinePower,
  optoIsolation,
} from "./constants";
import { debounce } from "lodash";

interface DiagramContextType {
  highlightedParts: string[];
  setHighlightedParts: any; // TODO

  handleMouseEnter: (elementIds: string[], callback?: () => void) => void;
  handleMouseLeave: (elementIds: string[]) => void;
  handleClick: (elementId: string) => void;
  keepFocused: any; // todo
  setKeepFocused: any; // TODO
  lastHoveredItem: any; // TODO
  isOverInfobox: any; // TODO
  setIsOverInfobox: any; // TODO
  handleHover: any; // TODO
  switchCamera: any; // TODO
  isDieCoverOpen: any; // TODO
  toggleDieCover: any; // TODO
}

const DiagramContext = createContext<DiagramContextType | undefined>(undefined);

export const DiagramProvider: React.FC<{
  highlightedParts: string[];
  setHighlightedParts: any;
  switchCamera: (cameraKey: string) => void; // TODO unify with other references to this function type
  isDieCoverOpen: any; // TODO,
  toggleDieCover: any; // TODO
  children: React.ReactNode;
}> = ({
  // TODO type
  highlightedParts,
  setHighlightedParts,
  switchCamera,
  isDieCoverOpen, // TODO
  toggleDieCover,
  children,
}) => {
  const [keepFocused, setKeepFocused] = useState<boolean>(false);
  const lastHoveredItem = useRef<string | null>(null);
  const [isOverInfobox, setIsOverInfobox] = useState<boolean>(false);

  const debouncedHandleHover = useMemo(
    () =>
      debounce((elementIds: string[] | null, callback?: () => void) => {
        if (!keepFocused) {
          setHighlightedParts(elementIds || []);
          lastHoveredItem.current = elementIds ? elementIds[0] : null;
          if (callback) {
            callback();
          }
        }
      }, 100),
    [keepFocused, setHighlightedParts],
  );

  const handleMouseEnter = (elementIds: string[], callback?: () => void) => {
    debouncedHandleHover(elementIds, callback);
  };

  const handleHover = (elementIds: string[] | null) => {
    if (!keepFocused) {
      setHighlightedParts(elementIds || []);
      lastHoveredItem.current = elementIds ? elementIds[0] : null;
    }
  };

  const handleMouseLeave = (elementIds: string[]) => {
    if (!isOverInfobox) {
      debouncedHandleHover(null);
    }
  };

  const handleClick = (elementId: string) => {
    setKeepFocused(true);
  };

  return (
    <DiagramContext.Provider
      value={{
        highlightedParts,
        setHighlightedParts,

        handleMouseEnter,
        handleMouseLeave,
        handleClick,
        keepFocused,
        setKeepFocused,
        lastHoveredItem,
        handleHover,
        isOverInfobox,
        setIsOverInfobox,
        switchCamera,
        isDieCoverOpen,
        toggleDieCover,
      }}
    >
      {children}
    </DiagramContext.Provider>
  );
};

export const useDiagramContext = () => {
  const context = useContext(DiagramContext);
  if (context === undefined) {
    throw new Error("useDiagramContext must be used within a DiagramProvider");
  }
  return context;
};

interface HoverableElementProps {
  elementIds: string[];
  children: React.ReactNode;
  onHoverCallback?: () => void;
}

export const HoverableElement: React.FC<HoverableElementProps> = ({
  elementIds,
  children,
  onHoverCallback,
}) => {
  const { highlightedParts, handleMouseEnter, handleMouseLeave, handleClick } =
    useDiagramContext();

  const isHighlighted = elementIds.some((id) => highlightedParts.includes(id));

  return (
    <g
      className={`hoverable ${isHighlighted ? "highlighted" : ""}`}
      onMouseEnter={() => handleMouseEnter(elementIds, onHoverCallback)}
      onMouseLeave={() => handleMouseLeave(elementIds)}
      onClick={() => handleClick(elementIds[0])}
    >
      {children}
    </g>
  );
};

type item = {
  name: string;
  link?: string;
};

type codeSample = {
  name: string;
  link?: string;
};

interface InfoboxContents {
  id: string;
  title: string;
  summary: string;
  implementation: string;
  code?: codeSample[];
  materials?: item[];
}

const Infobox: React.FC<{
  content: InfoboxContents | null;
  onClose: () => void;
  onInfoboxMouseEnter: () => void;
  onInfoboxMouseLeave: () => void;
  switchCamera: any; // TODO
}> = ({
  content,
  onClose,
  onInfoboxMouseEnter,
  onInfoboxMouseLeave,
  switchCamera,
}) => {
  if (!content) return null;

  return (
    <div
      className="infobox"
      onMouseEnter={onInfoboxMouseEnter}
      onMouseLeave={onInfoboxMouseLeave}
    >
      <button className="infobox-close" onClick={onClose}>
        ×
      </button>

      <div className="infobox-title">{content.title}</div>
      <div className="infobox-contents">{content.summary}</div>
      <div className="infobox-header">Implementation</div>
      <div className="infobox-contents">{content.implementation}</div>
      {content?.code && (
        <>
          <div className="infobox-header">Code</div>
          <div className="infobox-contents">
            {content.code.map((item, index) => (
              <a
                className="infobox-link"
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                {item.name}
              </a>
            ))}
          </div>
        </>
      )}
      {content?.materials && (
        <>
          <div className="infobox-header">Materials</div>
          <div className="infobox-contents">
            {content.materials.map((item, index) => (
              <a
                className="infobox-link"
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                {item.name}
              </a>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

function DiagramContent() {
  const {
    keepFocused,
    setKeepFocused,
    lastHoveredItem,
    handleHover,
    isOverInfobox,
    setIsOverInfobox,
    highlightedParts,
    setHighlightedParts,
    switchCamera,
    isDieCoverOpen,
    toggleDieCover,
  } = useDiagramContext();

  const [infoboxContents, setInfoboxContents] = useState<{
    [key: string]: InfoboxContents;
  }>({});
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          "https://api.airtable.com/v0/appV45JWQQiF94pSI/Components",
          {
            headers: {
              Authorization:
                "Bearer patgkP3VTwTqUMv6H.c6dbfb04ce65cb9f43fe08ce9ccd896c8d4fc8de5a1eb913f528c200650007e5",
            },
          },
        );
        const data = await response.json();

        const contents: { [key: string]: InfoboxContents } = {};
        data.records.forEach((record: any) => {
          contents[record.fields.id] = {
            id: record.fields.id,
            title: record.fields.Title,
            summary: record.fields.Summary,
            implementation: record.fields.Implementation,
            materials:
              record.fields.item_name && record.fields.item_link
                ? record.fields.item_name.map(
                    (name: string, index: number) => ({
                      name,
                      link: record.fields.item_link[index] || undefined,
                    }),
                  )
                : undefined,
            code:
              record.fields.code_name && record.fields.code_link
                ? record.fields.code_name.map(
                    (name: string, index: number) => ({
                      name,
                      link: record.fields.item_link[index] || undefined,
                    }),
                  )
                : undefined,
          };
        });

        setInfoboxContents(contents);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data from Airtable:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const anythingHighlighted = highlightedParts.length > 0;
  const currentInfoboxContent = anythingHighlighted
    ? infoboxContents[highlightedParts[0]]
    : null;

  const getStyle = (fill: string, fontSize: string) => ({
    fill,
    fontFamily: "Andale Mono, PingFang SC, Mircrosoft YaHei, Arial",
    fontSize,
  });

  const handleInfoboxMouseEnter = () => {
    console.log("handleInfoboxMouseEnter");
    setIsOverInfobox(true);
  };

  const handleInfoboxMouseLeave = () => {
    console.log("handleInfoboxMouseLeave");
    setIsOverInfobox(false);
    if (!keepFocused) {
      console.log("!keepFocused, calling handleHover(null)");
      handleHover(null);
    }
  };

  const handleClose = () => {
    setKeepFocused(false);
    setHighlightedParts([]);
    lastHoveredItem.current = null;
  };

  const borderColor = "#b2b2b2";

  return (
    <div className="diagram-container">
      {anythingHighlighted &&
        (keepFocused ? (
          <div
            className="helper-text click-to-unfocus-text"
            onClick={() => {
              setKeepFocused(false);
              setHighlightedParts([]);
            }}
          >
            (X) Click to unfocus
          </div>
        ) : (
          <div className="helper-text">Click to keep item focused</div>
        ))}
      <Infobox
        content={currentInfoboxContent}
        onClose={handleClose}
        onInfoboxMouseEnter={handleInfoboxMouseEnter}
        onInfoboxMouseLeave={handleInfoboxMouseLeave}
        switchCamera={switchCamera}
      />

      <div
        id="svg-container"
        className={anythingHighlighted ? "element-hovered" : ""}
      >
        <svg
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width="100%"
          id="page1"
          viewBox="0 0 1123 794"
          preserveAspectRatio="xMinYMin meet"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs />
          <path
            strokeWidth="1.333333373069763"
            id="shape1"
            fill="#ffffff"
            fillRule="nonzero"
            transform="translate(35.778761,38.000000)"
            stroke={borderColor}
            d="M779.2,712.2L779.2,11.0C779.2,4.9,774.3,.0,768.2,.0L11.0,.0C4.9,.0,.0,4.9,.0,11.0L.0,712.2C.0,718.2,4.9,723.2,11.0,723.2L768.2,723.2C774.3,723.2,779.2,718.2,779.2,712.2z"
          />
          <g id="group2" transform="translate(856.898436,577.206664)">
            <path
              strokeWidth="0.5"
              id="shape3"
              fill="#d6d8df"
              fillRule="nonzero"
              transform="translate(0.000000,24.987554)"
              stroke="#d6d8df"
              d="M.0,.0L50.9,27.3L50.9,39.2L.0,11.9L.0,.0z"
            />
            <path
              strokeWidth="1"
              id="shape4"
              fill="none"
              transform="translate(0.203067,31.519795)"
              stroke="#000000"
              strokeDasharray="3.8,1.2"
              d="M.0,.0L44.6,24.1"
            />
            <path
              strokeWidth="1"
              id="shape5"
              fill="#ffffff"
              fillRule="nonzero"
              transform="translate(19.667217,39.357636)"
              stroke="#ffffff"
              d="M.0,.0L10.0,5.3L10.0,10.7L.0,5.3L.0,.0z"
            />
            <path
              strokeWidth="0.5"
              id="shape6"
              fill="#caccd3"
              fillRule="nonzero"
              transform="matrix(-1.000000,0.000000,0.000000,1.000000,98.355081,27.204191)"
              stroke="#caccd3"
              d="M.0,.0L47.4,25.1L47.4,36.9L.0,11.9L.0,.0z"
            />
            <path
              strokeWidth="0.5"
              id="shape7"
              fill="#e2e4e9"
              fillRule="nonzero"
              transform="translate(0.000000,-0.000000)"
              stroke="#e2e4e9"
              d="M50.9,52.3L.0,25.0L47.4,.0L98.4,27.2L50.9,52.3z"
            />
            <path
              strokeWidth="1"
              id="shape8"
              fill="#62bc85"
              fillRule="nonzero"
              transform="translate(20.084682,40.216033)"
              stroke="#62bc85"
              d="M.0,.0L9.1,4.8L9.1,9.0L.0,4.1L.0,.0z"
            />
            <path
              strokeWidth="0.25"
              id="shape9"
              fill="#e85464"
              fillRule="nonzero"
              transform="matrix(0.999391,-0.034899,0.034899,0.999391,43.392539,52.632655)"
              stroke="#e85464"
              d="M.0,3.1C-0.1,1.5,.8,.1,2.0,.0C3.3,-0.1,4.4,1.1,4.5,2.7C4.7,4.3,3.8,5.7,2.5,5.8C1.3,5.9,.2,4.7,.0,3.1z"
            />
            <path
              strokeWidth="0.4799999892711639"
              id="shape10"
              fill="#ff7878"
              fillRule="nonzero"
              transform="matrix(0.999391,-0.034899,0.034899,0.999391,43.872567,53.207594)"
              stroke="#ff7878"
              d="M.0,2.5C-0.1,1.2,.6,.1,1.6,.0C2.6,-0.1,3.5,.9,3.6,2.2C3.7,3.5,3.0,4.6,2.0,4.7C1.0,4.7,.1,3.8,.0,2.5z"
            />
            <path
              id="shape11"
              fill="#ffffff"
              fillRule="nonzero"
              transform="matrix(0.999391,-0.034899,0.034899,0.999391,43.863751,53.572851)"
              fillOpacity="0.12"
              d="M.0,2.1C-0.0,1.2,.3,.5,.8,.0C.8,.0,3.0,3.8,3.0,3.8C2.7,4.1,2.4,4.3,2.0,4.3C1.0,4.4,.1,3.4,.0,2.1z"
            />
            <path
              id="shape12"
              fill="#ffffff"
              fillRule="nonzero"
              transform="translate(20.295664,40.559908)"
              fillOpacity="0.29"
              d="M.0,.0L8.7,4.5L8.7,8.4L.0,3.7L.0,.0z"
            />
          </g>
          <g id="group13" transform="translate(925.898436,193.356270)">
            <path
              strokeWidth="0.5"
              id="shape14"
              fill="#d6d8df"
              fillRule="nonzero"
              transform="translate(0.000000,28.827513)"
              stroke="#d6d8df"
              d="M.0,.0L59.1,31.5L59.1,45.2L.0,13.7L.0,.0z"
            />
            <path
              strokeWidth="1"
              id="shape15"
              fill="none"
              transform="translate(0.235368,36.363594)"
              stroke="#000000"
              strokeDasharray="3.8,1.2"
              d="M.0,.0L51.7,27.8"
            />
            <path
              strokeWidth="1"
              id="shape16"
              fill="#ffffff"
              fillRule="nonzero"
              transform="translate(22.795597,45.405914)"
              stroke="#ffffff"
              d="M.0,.0L11.6,6.1L11.6,12.4L.0,6.1L.0,.0z"
            />
            <path
              strokeWidth="0.5"
              id="shape17"
              fill="#caccd3"
              fillRule="nonzero"
              transform="matrix(-1.000000,0.000000,0.000000,1.000000,114.000000,31.384791)"
              stroke="#caccd3"
              d="M.0,.0L54.9,28.9L54.9,42.6L.0,13.7L.0,.0z"
            />
            <path
              strokeWidth="0.5"
              id="shape18"
              fill="#e2e4e9"
              fillRule="nonzero"
              transform="translate(0.000000,-0.000000)"
              stroke="#e2e4e9"
              d="M59.1,60.3L.0,28.8L54.9,.0L114.0,31.4L59.1,60.3z"
            />
            <path
              strokeWidth="1"
              id="shape19"
              fill="#62bc85"
              fillRule="nonzero"
              transform="translate(23.279466,46.396225)"
              stroke="#62bc85"
              d="M.0,.0L10.5,5.6L10.5,10.4L.0,4.7L.0,.0z"
            />
            <path
              strokeWidth="0.25"
              id="shape20"
              fill="#e85464"
              fillRule="nonzero"
              transform="matrix(0.999391,-0.034899,0.034899,0.999391,50.295354,60.721397)"
              stroke="#e85464"
              d="M.0,3.6C-0.1,1.7,.9,.1,2.3,.0C3.8,-0.1,5.1,1.3,5.2,3.1C5.4,5.0,4.4,6.6,2.9,6.7C1.5,6.9,.2,5.5,.0,3.6z"
            />
            <path
              strokeWidth="0.4799999892711639"
              id="shape21"
              fill="#ff7878"
              fillRule="nonzero"
              transform="matrix(0.999391,-0.034899,0.034899,0.999391,50.851627,61.384603)"
              stroke="#ff7878"
              d="M.0,2.9C-0.1,1.4,.7,.1,1.9,.0C3.0,-0.1,4.1,1.0,4.2,2.5C4.3,4.0,3.5,5.3,2.3,5.4C1.2,5.5,.1,4.4,.0,2.9z"
            />
            <path
              id="shape22"
              fill="#ffffff"
              fillRule="nonzero"
              transform="matrix(0.999391,-0.034899,0.034899,0.999391,50.841374,61.805929)"
              fillOpacity="0.12"
              d="M.0,2.4C-0.1,1.4,.3,.5,.9,.0C.9,.0,3.4,4.4,3.4,4.4C3.1,4.7,2.8,4.9,2.4,5.0C1.2,5.1,.2,3.9,.0,2.4z"
            />
            <path
              id="shape23"
              fill="#ffffff"
              fillRule="nonzero"
              transform="translate(23.524007,46.792945)"
              fillOpacity="0.29"
              d="M.0,.0L10.1,5.2L10.1,9.6L.0,4.3L.0,.0z"
            />
          </g>
          <g id="shape24" transform="translate(982.065102,384.407974)">
            <path
              strokeWidth="1"
              fill="none"
              stroke="#191919"
              d="M.0,-6.0L.8,-111.1"
            />
            <path
              strokeWidth="1"
              fill="#191919"
              stroke="#191919"
              d="M3.0,-6.0L.0,.0L-3.0,-6.0L3.0,-6.0"
              strokeLinecap="round"
            />
            <path
              strokeWidth="1"
              fill="#191919"
              stroke="#191919"
              d="M-2.2,-111.1L.8,-117.1L3.8,-111.0L-2.2,-111.1"
              strokeLinecap="round"
            />
          </g>
          <path
            strokeWidth="1.333333373069763"
            id="shape25"
            fill="#ffffff"
            fillRule="nonzero"
            transform="translate(910.898436,181.956270)"
            stroke={borderColor}
            fillOpacity="0.00"
            d="M.0,.0L142.3,.0L142.3,96.4L.0,96.4L.0,.0z"
          />
          <path
            strokeWidth="1"
            id="shape26"
            fill="#ffffff"
            fillRule="nonzero"
            transform="translate(76.000000,76.000000)"
            stroke="#c00000"
            d="M.0,.0L699.0,.0L699.0,642.0L.0,642.0L.0,.0z"
          />

          <HoverableElement elementIds={[secure_processor]}>
            <path
              strokeWidth="1.333333373069763"
              id="shape28"
              fill="#ebebeb"
              fillRule="nonzero"
              transform="translate(567.000000,283.709375)"
              stroke="#323232"
              d="M.0,.0L191.6,.0L191.6,96.4L.0,96.4L.0,.0z"
            />
            <g id="shape29" transform="translate(567.000000,283.709375)">
              <text style={getStyle("#191919", "18pt")} xmlSpace="preserve">
                <tspan x="52.6" y="43.2">
                  Secure
                </tspan>
                <tspan x="31.0" y="70.2">
                  processor
                </tspan>
              </text>
            </g>
          </HoverableElement>

          <g id="shape30" transform="translate(534.000000,717.656270)">
            <text style={getStyle("#c00000", "18pt")} xmlSpace="preserve">
              <tspan x="11.8" y="30.1">
                Secure enclosure
              </tspan>
            </text>
          </g>

          <HoverableElement elementIds={[AI_processor]}>
            <path
              strokeWidth="1.333333373069763"
              id="shape27"
              fill="#ebebeb"
              fillRule="nonzero"
              transform="translate(95.746944,262.000000)"
              stroke="#323232"
              d="M.0,.0L270.3,.0L270.3,371.0L.0,371.0L.0,.0z"
            />
            <g id="shape31" transform="translate(95.746944,266.200000)">
              <text style={getStyle("#191919", "24pt")} xmlSpace="preserve">
                <tspan x="19.9" y="56.0">
                  AI processor
                </tspan>
              </text>
            </g>

            <g id="shape121" transform="translate(135.811772,407.304688)">
              <path
                strokeWidth="1"
                fill="#ffffff"
                fillRule="nonzero"
                stroke="#000000"
                d="M.0,.0L190.1,.0L190.1,64.7L.0,64.7L.0,.0z"
              />
              <text style={getStyle("#191919", "18pt")} xmlSpace="preserve">
                <tspan x="51.9" y="40.8">
                  Memory
                </tspan>
              </text>
            </g>
            {/* </g> */}
          </HoverableElement>

          <HoverableElement
            elementIds={[
              self_disable,
              "12 Met3",
              "13 Via3",
              "14 Met4",
              "16 Tap",
              "2 Nwell",
              "4 Poly",
              "5 Licon",
              "6 Li1",
              "7 Mcon",
              "9 Via",
              "Atum A5 Zoomed Byte",
              "Atum A5 Parent",
              "Atum A5 Chip",
            ]}
            onHoverCallback={() => {
              if (!isDieCoverOpen) {
                toggleDieCover();
              }
              switchCamera("insideChip");
            }}
          >
            <g id="shape85" transform="translate(135.811772,495.051450)">
              <path
                strokeWidth="1"
                fill="#ffffff"
                fillRule="nonzero"
                stroke="#000000"
                d="M.0,.0L190.1,.0L190.1,52.0L.0,52.0L.0,.0z"
              />
              <text style={getStyle("#191919", "14pt")} xmlSpace="preserve">
                <tspan x="21.0" y="22.0">
                  self-disable
                </tspan>
                <tspan x="43.8" y="44.0">
                  mechanism
                </tspan>
              </text>
            </g>
          </HoverableElement>

          <HoverableElement
            elementIds={[
              antiTamper,
              "Metal Foam",
              "DWM 1001 Parent",
              "Blue Shield Waves",
              "Red Shield Waves",
              "Blue Shield Waves Top",
              "Red Shield Waves Top",
              "Needle Attack",
            ]}
          >
            <g id="shape32" transform="translate(492.759843,441.000000)">
              <path
                strokeWidth="1"
                fill="none"
                stroke="#c00000"
                d="M.0,.0L137.0,.0L137.0,55.0L.0,55.0L.0,.0z"
              />
              <text style={getStyle("#c00000", "14pt")} xmlSpace="preserve">
                <tspan x="5.8" y="23.5">
                  anti-tamper
                </tspan>
                <tspan x="28.6" y="45.5">
                  sensors
                </tspan>
              </text>
            </g>
            <path
              strokeWidth="1"
              id="shape34"
              fill="#ffffff"
              fillRule="nonzero"
              transform="translate(535.000000,407.304688)"
              stroke="#c00000"
              d="M.0,7.9C.0,3.6,3.6,.0,8.1,.0C12.6,.0,16.3,3.6,16.3,7.9C16.3,12.3,12.6,15.9,8.1,15.9C3.6,15.9,.0,12.3,.0,7.9z"
            />
            <path
              strokeWidth="1"
              id="shape118"
              fill="#ffffff"
              fillRule="nonzero"
              transform="translate(606.000000,513.102900)"
              stroke="#c00000"
              d="M.0,7.9C.0,3.6,3.6,.0,8.1,.0C12.6,.0,16.3,3.6,16.3,7.9C16.3,12.3,12.6,15.9,8.1,15.9C3.6,15.9,.0,12.3,.0,7.9z"
            />
            <path
              strokeWidth="1"
              id="shape124"
              fill="none"
              transform="translate(492.759843,468.500000)"
              stroke="#c00000"
              d="M.0,.0L-139.8,.0L-139.8,52.6L-166.8,52.6"
            />
          </HoverableElement>

          <g id="shape33" transform="translate(140.311772,718.000000)">
            <text style={getStyle("#191919", "14pt")} xmlSpace="preserve">
              <tspan x="22.2" y="28.6">
                Power supply
              </tspan>
            </text>
          </g>

          <path
            strokeWidth="1"
            id="shape35"
            fill="none"
            transform="matrix(0.000000,1.000000,-1.000000,0.000000,467.000000,76.000000)"
            stroke="#868686"
            strokeDasharray="11,5"
            d="M.0,.0L642.0,.0"
          />

          <HoverableElement
            elementIds={[offlinePower, "Battery 1"]}
            onHoverCallback={() => switchCamera("enclosure")}
          >
            {/* <g
                className={`hoverable ${
                  highlightedParts.includes(offlinePower) ? "highlighted" : ""
                }`}
                onMouseEnter={() => handleHover(offlinePower)}
                onMouseLeave={() => handleHover(null)}
                onClick={() => handleClick(offlinePower)}
              > */}
            <g id="shape37" transform="translate(567.000000,628.238498)">
              <path
                strokeWidth="1"
                fill="#ffffff"
                fillRule="nonzero"
                stroke="#323232"
                d="M.0,.0L191.6,.0L191.6,81.8L.0,81.8L.0,.0z"
              />
              <text style={getStyle("#191919", "14pt")} xmlSpace="preserve">
                <tspan x="55.9" y="36.9">
                  Battery
                </tspan>
                <tspan x="10.3" y="58.9">
                  (offline power)
                </tspan>
              </text>
            </g>
            {/* </g> */}
          </HoverableElement>

          <HoverableElement elementIds={[router]}>
            {/* <g
                className={`hoverable ${
                  highlightedParts.includes(router) ? "highlighted" : ""
                }`}
                onMouseEnter={() => handleHover(router)}
                onMouseLeave={() => handleHover(null)}
                onClick={() => handleClick(router)}
              > */}
            <g id="group38" transform="translate(935.816300,384.407974)">
              <path
                strokeWidth="0.5"
                id="shape39"
                fill="#e2e4e9"
                fillRule="nonzero"
                transform="translate(0.000000,-0.000000)"
                stroke="#e2e4e9"
                d="M43.8,48.1L.0,24.0L50.8,.0L92.5,20.3L43.8,48.1z"
              />
              <path
                strokeWidth="0.5"
                id="shape40"
                fill="#d6d8df"
                fillRule="nonzero"
                transform="translate(0.000000,23.885968)"
                stroke="#d6d8df"
                d="M.0,.0L43.6,24.0L43.6,35.1L.0,11.0L.0,.0z"
              />
              <path
                strokeWidth="0.5"
                id="shape41"
                fill="#caccd3"
                fillRule="nonzero"
                transform="translate(43.422690,20.302991)"
                stroke="#caccd3"
                d="M.0,27.5L49.1,.0L49.1,11.0L.0,38.5L.0,27.5z"
              />
              <path
                strokeWidth="0.5"
                id="shape42"
                fill="#e2e4e9"
                fillRule="nonzero"
                transform="translate(45.266776,42.290176)"
                stroke="#e2e4e9"
                d="M.0,7.8L13.5,.0L13.5,2.0L.0,9.8L.0,7.8z"
              />
              <g id="group43" transform="translate(21.094233,9.518443)">
                <path
                  strokeWidth="1"
                  id="shape44"
                  fill="#e85464"
                  fillRule="nonzero"
                  transform="translate(-0.000000,11.169968)"
                  stroke="#e85464"
                  d="M5.8,.0L11.0,2.8L2.7,7.3C2.7,7.3,-1.4,5.7,.5,2.6L5.8,.0z"
                />
                <path
                  strokeWidth="1"
                  id="shape45"
                  fill="#e85464"
                  fillRule="nonzero"
                  transform="translate(21.838199,-0.000000)"
                  stroke="#e85464"
                  d="M.0,3.2L4.4,1.0C4.4,1.0,6.8,-0.5,9.9,.2C9.9,.2,11.4,.5,12.8,1.6L4.5,5.7L.0,3.2z"
                />
                <path
                  strokeWidth="1"
                  id="shape46"
                  fill="#e85464"
                  fillRule="nonzero"
                  transform="translate(9.883343,4.990930)"
                  stroke="#e85464"
                  d="M8.2,.0L12.9,2.6L4.9,6.8L.0,4.2L8.2,.0z"
                />
                <path
                  strokeWidth="1"
                  id="shape47"
                  fill="#e85464"
                  fillRule="nonzero"
                  transform="translate(17.319368,21.865598)"
                  stroke="#e85464"
                  d="M.0,4.1L7.6,.0L12.6,2.2L6.3,5.3C6.3,5.3,3.9,6.1,.0,4.1z"
                />
                <path
                  strokeWidth="1"
                  id="shape48"
                  fill="#e85464"
                  fillRule="nonzero"
                  transform="translate(40.027209,8.001991)"
                  stroke="#e85464"
                  d="M.0,4.6L8.7,.0C8.7,.0,11.8,1.5,10.4,5.0L5.5,7.5L.0,4.6z"
                />
                <path
                  strokeWidth="1"
                  id="shape49"
                  fill="#e85464"
                  fillRule="nonzero"
                  transform="translate(6.906729,16.445000)"
                  stroke="#e85464"
                  d="M7.9,.0L13.0,2.8L5.5,7.0L.0,4.2L7.9,.0z"
                />
                <path
                  strokeWidth="1"
                  id="shape50"
                  fill="#e85464"
                  fillRule="nonzero"
                  transform="translate(30.629263,3.615856)"
                  stroke="#e85464"
                  d="M8.2,.0L12.9,2.6L4.9,6.8L.0,4.2L8.2,.0z"
                />
                <path
                  strokeWidth="1"
                  id="shape51"
                  fill="#e85464"
                  fillRule="nonzero"
                  transform="translate(29.024584,14.862148)"
                  stroke="#e85464"
                  d="M8.2,.0L12.9,2.6L4.9,6.8L.0,4.2L8.2,.0z"
                />
              </g>
              <path
                strokeWidth="0.5"
                id="shape52"
                fill="#e2e4e9"
                fillRule="nonzero"
                transform="translate(61.256118,32.914260)"
                stroke="#e2e4e9"
                d="M.0,7.8L13.5,.0L13.5,2.0L.0,9.8L.0,7.8z"
              />
              <path
                strokeWidth="0.5"
                id="shape53"
                fill="#e2e4e9"
                fillRule="nonzero"
                transform="translate(77.245460,23.884445)"
                stroke="#e2e4e9"
                d="M.0,7.8L13.5,.0L13.5,2.0L.0,9.8L.0,7.8z"
              />
              <path
                strokeWidth="1"
                id="shape54"
                fill="#595757"
                fillRule="nonzero"
                transform="translate(61.816016,42.864494)"
                stroke="#595757"
                d="M.0,1.4L2.4,.0L2.4,2.3L.0,3.7L.0,1.4z"
              />
              <path
                strokeWidth="1"
                id="shape55"
                fill="#595757"
                fillRule="nonzero"
                transform="translate(69.085291,38.593081)"
                stroke="#595757"
                d="M.0,1.4L2.4,.0L2.4,2.3L.0,3.7L.0,1.4z"
              />
              <path
                strokeWidth="1"
                id="shape56"
                fill="#595757"
                fillRule="nonzero"
                transform="translate(72.719928,36.579138)"
                stroke="#595757"
                d="M.0,1.4L2.4,.0L2.4,2.3L.0,3.7L.0,1.4z"
              />
              <path
                strokeWidth="1"
                id="shape57"
                fill="#595757"
                fillRule="nonzero"
                transform="translate(65.450653,40.827554)"
                stroke="#595757"
                d="M.0,1.4L2.4,.0L2.4,2.3L.0,3.7L.0,1.4z"
              />
            </g>
            <g id="shape116" transform="translate(997.644919,443.356270)">
              <text style={getStyle("#191919", "18pt")} xmlSpace="preserve">
                <tspan x="4.2" y="25.9">
                  Router
                </tspan>
              </text>
            </g>
            {/* </g> */}
          </HoverableElement>

          <g id="shape58" transform="translate(982.065102,443.356270)">
            <path
              strokeWidth="1"
              fill="none"
              stroke="#191919"
              d="M.0,6.0L.0,75.0L-76.0,75.0L-76.0,127.9"
            />
            <path
              strokeWidth="1"
              fill="#191919"
              stroke="#191919"
              d="M-3.0,6.0L.0,.0L3.0,6.0L-3.0,6.0"
              strokeLinecap="round"
            />
            <path
              strokeWidth="1"
              fill="#191919"
              stroke="#191919"
              d="M-73.0,127.9L-76.0,133.9L-79.0,127.9L-73.0,127.9"
              strokeLinecap="round"
            />
          </g>
          <g id="shape59" transform="translate(982.625159,441.347289)">
            <path
              strokeWidth="1"
              fill="none"
              stroke="#191919"
              d="M.0,.0L.0,77.0L64.2,77.0L64.2,127.4"
            />
            <path
              strokeWidth="1"
              fill="#191919"
              stroke="#191919"
              d="M67.2,127.4L64.2,133.4L61.2,127.4L67.2,127.4"
              strokeLinecap="round"
            />
          </g>
          <HoverableElement elementIds={[galvanic]}>
            {/* <g
                className={`hoverable ${
                  highlightedParts.includes(galvanic) ? "highlighted" : ""
                }`}
                onMouseEnter={() => handleHover(galvanic)}
                onMouseLeave={() => handleHover(null)}
                onClick={() => handleClick(galvanic)}
              > */}
            <g id="shape36" transform="translate(370.763789,91.000000)">
              <path
                fill="#ffffff"
                fillRule="nonzero"
                d="M.0,.0L194.0,.0L194.0,71.0L.0,71.0L.0,.0z"
              />
              <text style={getStyle("#868686", "14pt")} xmlSpace="preserve">
                <tspan x="4.0" y="20.5">
                  Power isolation
                </tspan>
                <tspan x="4.0" y="42.5">
                  Untrusted (left)
                </tspan>
                <tspan x="4.0" y="64.5">
                  trusted (right)
                </tspan>
              </text>
            </g>
            <g id="shape60" transform="translate(395.445046,640.071452)">
              <path
                strokeWidth="1"
                fill="#ffffff"
                fillRule="nonzero"
                stroke="#868686"
                d="M.0,.0L137.0,.0L137.0,58.9L.0,58.9L.0,.0z"
              />
              <text style={getStyle("#868686", "14pt")} xmlSpace="preserve">
                <tspan x="22.9" y="25.5">
                  galvanic
                </tspan>
                <tspan x="17.2" y="47.5">
                  isolation
                </tspan>
              </text>
            </g>
            {/* </g> */}
          </HoverableElement>

          <HoverableElement
            elementIds={[optoIsolation, "Atum A5 Wire", "Atum A5 Signal"]}
            onHoverCallback={() => switchCamera("system")}
          >
            {/* <g
                className={`hoverable ${
                  highlightedParts.includes(optoIsolation) ? "highlighted" : ""
                }`}
                onMouseEnter={() => handleHover(optoIsolation)}
                onMouseLeave={() => handleHover(null)}
                onClick={() => handleClick(optoIsolation)}
              > */}
            <g id="shape36" transform="translate(370.763789,91.000000)">
              <path
                fill="#ffffff"
                fillRule="nonzero"
                d="M.0,.0L194.0,.0L194.0,71.0L.0,71.0L.0,.0z"
              />
              <text style={getStyle("#868686", "14pt")} xmlSpace="preserve">
                <tspan x="4.0" y="20.5">
                  Power isolation
                </tspan>
                <tspan x="4.0" y="42.5">
                  Untrusted (left)
                </tspan>
                <tspan x="4.0" y="64.5">
                  trusted (right)
                </tspan>
              </text>
            </g>
            <g id="shape61" transform="translate(404.445046,309.618750)">
              <path
                strokeWidth="1"
                fill="#ffffff"
                fillRule="nonzero"
                stroke="#868686"
                d="M.0,.0L137.0,.0L137.0,44.6L.0,44.6L.0,.0z"
              />
              <text style={getStyle("#868686", "14pt")} xmlSpace="preserve">
                <tspan x="40.0" y="18.3">
                  opto-
                </tspan>
                <tspan x="17.2" y="40.3">
                  isolation
                </tspan>
              </text>
            </g>
          </HoverableElement>

          <g id="group62" transform="translate(988.372736,640.506270)">
            <path
              strokeWidth="0.5"
              id="shape63"
              fill="#d6d8df"
              fillRule="nonzero"
              transform="translate(0.000000,12.017980)"
              stroke="#d6d8df"
              d="M.0,.0L24.6,13.1L24.6,18.8L.0,5.7L.0,.0z"
            />
            <path
              strokeWidth="1"
              id="shape64"
              fill="none"
              transform="translate(0.098100,15.159720)"
              stroke="#000000"
              strokeDasharray="3.8,1.2"
              d="M.0,.0L21.6,11.6"
            />
            <path
              strokeWidth="1"
              id="shape65"
              fill="#ffffff"
              fillRule="nonzero"
              transform="translate(9.503350,18.929320)"
              stroke="#ffffff"
              d="M.0,.0L4.8,2.6L4.8,5.2L.0,2.5L.0,.0z"
            />
            <path
              strokeWidth="0.5"
              id="shape66"
              fill="#caccd3"
              fillRule="nonzero"
              transform="matrix(-1.000000,0.000000,0.000000,1.000000,47.525700,13.084040)"
              stroke="#caccd3"
              d="M.0,.0L22.9,12.1L22.9,17.8L.0,5.7L.0,.0z"
            />
            <path
              strokeWidth="0.5"
              id="shape67"
              fill="#e2e4e9"
              fillRule="nonzero"
              stroke="#e2e4e9"
              d="M24.6,25.1L.0,12.0L22.9,.0L47.5,13.1L24.6,25.1z"
            />
            <path
              strokeWidth="1"
              id="shape68"
              fill="#62bc85"
              fillRule="nonzero"
              transform="translate(9.705000,19.342170)"
              stroke="#62bc85"
              d="M.0,.0L4.4,2.3L4.4,4.3L.0,2.0L.0,.0z"
            />
            <path
              strokeWidth="0.25"
              id="shape69"
              fill="#e85464"
              fillRule="nonzero"
              transform="matrix(0.999391,-0.034899,0.034899,0.999391,20.967709,25.314248)"
              stroke="#e85464"
              d="M.0,1.5C-0.1,.7,.4,.1,1.0,.0C1.6,-0.0,2.1,.5,2.2,1.3C2.3,2.1,1.8,2.8,1.2,2.8C.6,2.9,.1,2.3,.0,1.5z"
            />
            <path
              strokeWidth="0.4799999892711639"
              id="shape70"
              fill="#ff7878"
              fillRule="nonzero"
              transform="matrix(0.999391,-0.034899,0.034899,0.999391,21.199618,25.590713)"
              stroke="#ff7878"
              d="M.0,1.2C-0.0,.6,.3,.0,.8,.0C1.3,-0.0,1.7,.4,1.7,1.0C1.8,1.7,1.5,2.2,1.0,2.2C.5,2.3,.1,1.8,.0,1.2z"
            />
            <path
              id="shape71"
              fill="#ffffff"
              fillRule="nonzero"
              transform="matrix(0.999391,-0.034899,0.034899,0.999391,21.195413,25.766444)"
              fillOpacity="0.12"
              d="M.0,1.0C-0.0,.6,.1,.2,.4,.0C.4,.0,1.4,1.8,1.4,1.8C1.3,2.0,1.2,2.1,1.0,2.1C.5,2.1,.1,1.6,.0,1.0z"
            />
            <path
              id="shape72"
              fill="#ffffff"
              fillRule="nonzero"
              transform="translate(9.806950,19.507590)"
              fillOpacity="0.29"
              d="M.0,.0L4.2,2.2L4.2,4.0L.0,1.8L.0,.0z"
            />
          </g>
          <g id="group73" transform="translate(958.135586,663.931270)">
            <path
              strokeWidth="0.5"
              id="shape74"
              fill="#d6d8df"
              fillRule="nonzero"
              transform="translate(0.000000,12.017980)"
              stroke="#d6d8df"
              d="M.0,.0L24.6,13.1L24.6,18.8L.0,5.7L.0,.0z"
            />
            <path
              strokeWidth="1"
              id="shape75"
              fill="none"
              transform="translate(0.098100,15.159720)"
              stroke="#000000"
              strokeDasharray="3.8,1.2"
              d="M.0,.0L21.6,11.6"
            />
            <path
              strokeWidth="1"
              id="shape76"
              fill="#ffffff"
              fillRule="nonzero"
              transform="translate(9.503350,18.929320)"
              stroke="#ffffff"
              d="M.0,.0L4.8,2.6L4.8,5.2L.0,2.5L.0,.0z"
            />
            <path
              strokeWidth="0.5"
              id="shape77"
              fill="#caccd3"
              fillRule="nonzero"
              transform="matrix(-1.000000,0.000000,0.000000,1.000000,47.525700,13.084040)"
              stroke="#caccd3"
              d="M.0,.0L22.9,12.1L22.9,17.8L.0,5.7L.0,.0z"
            />
            <path
              strokeWidth="0.5"
              id="shape78"
              fill="#e2e4e9"
              fillRule="nonzero"
              stroke="#e2e4e9"
              d="M24.6,25.1L.0,12.0L22.9,.0L47.5,13.1L24.6,25.1z"
            />
            <path
              strokeWidth="1"
              id="shape79"
              fill="#62bc85"
              fillRule="nonzero"
              transform="translate(9.705000,19.342170)"
              stroke="#62bc85"
              d="M.0,.0L4.4,2.3L4.4,4.3L.0,2.0L.0,.0z"
            />
            <path
              strokeWidth="0.25"
              id="shape80"
              fill="#e85464"
              fillRule="nonzero"
              transform="matrix(0.999391,-0.034899,0.034899,0.999391,20.967709,25.314248)"
              stroke="#e85464"
              d="M.0,1.5C-0.1,.7,.4,.1,1.0,.0C1.6,-0.0,2.1,.5,2.2,1.3C2.3,2.1,1.8,2.8,1.2,2.8C.6,2.9,.1,2.3,.0,1.5z"
            />
            <path
              strokeWidth="0.4799999892711639"
              id="shape81"
              fill="#ff7878"
              fillRule="nonzero"
              transform="matrix(0.999391,-0.034899,0.034899,0.999391,21.199618,25.590713)"
              stroke="#ff7878"
              d="M.0,1.2C-0.0,.6,.3,.0,.8,.0C1.3,-0.0,1.7,.4,1.7,1.0C1.8,1.7,1.5,2.2,1.0,2.2C.5,2.3,.1,1.8,.0,1.2z"
            />
            <path
              id="shape82"
              fill="#ffffff"
              fillRule="nonzero"
              transform="matrix(0.999391,-0.034899,0.034899,0.999391,21.195413,25.766444)"
              fillOpacity="0.12"
              d="M.0,1.0C-0.0,.6,.1,.2,.4,.0C.4,.0,1.4,1.8,1.4,1.8C1.3,2.0,1.2,2.1,1.0,2.1C.5,2.1,.1,1.6,.0,1.0z"
            />
            <path
              id="shape83"
              fill="#ffffff"
              fillRule="nonzero"
              transform="translate(9.806950,19.507590)"
              fillOpacity="0.29"
              d="M.0,.0L4.2,2.2L4.2,4.0L.0,1.8L.0,.0z"
            />
          </g>

          <path
            strokeWidth="1.333333373069763"
            id="shape86"
            fill="none"
            transform="translate(910.898436,230.156270)"
            stroke={borderColor}
            d="M.0,.0L-68.0,.0L-68.0,169.4L-96.0,169.4"
          />

          <g id="group88" transform="translate(997.644900,574.752505)">
            <path
              strokeWidth="0.5"
              id="shape89"
              fill="#d6d8df"
              fillRule="nonzero"
              transform="translate(0.000000,24.987500)"
              stroke="#d6d8df"
              d="M.0,.0L50.9,27.3L50.9,39.2L.0,11.9L.0,.0z"
            />
            <path
              strokeWidth="1"
              id="shape90"
              fill="none"
              transform="translate(0.203100,31.519800)"
              stroke="#000000"
              strokeDasharray="3.8,1.2"
              d="M.0,.0L44.6,24.1"
            />
            <path
              strokeWidth="1"
              id="shape91"
              fill="#ffffff"
              fillRule="nonzero"
              transform="translate(19.667180,39.357680)"
              stroke="#ffffff"
              d="M.0,.0L10.0,5.3L10.0,10.7L.0,5.3L.0,.0z"
            />
            <path
              strokeWidth="0.5"
              id="shape92"
              fill="#caccd3"
              fillRule="nonzero"
              transform="matrix(-1.000000,0.000000,0.000000,1.000000,98.355100,27.204200)"
              stroke="#caccd3"
              d="M.0,.0L47.4,25.1L47.4,36.9L.0,11.9L.0,.0z"
            />
            <path
              strokeWidth="0.5"
              id="shape93"
              fill="#e2e4e9"
              fillRule="nonzero"
              stroke="#e2e4e9"
              d="M50.9,52.3L.0,25.0L47.4,.0L98.4,27.2L50.9,52.3z"
            />
            <path
              strokeWidth="1"
              id="shape94"
              fill="#62bc85"
              fillRule="nonzero"
              transform="translate(20.084680,40.216070)"
              stroke="#62bc85"
              d="M.0,.0L9.1,4.8L9.1,9.0L.0,4.1L.0,.0z"
            />
            <path
              strokeWidth="0.25"
              id="shape95"
              fill="#e85464"
              fillRule="nonzero"
              transform="matrix(0.999391,-0.034899,0.034899,0.999391,43.392519,52.632675)"
              stroke="#e85464"
              d="M.0,3.1C-0.1,1.5,.8,.1,2.0,.0C3.3,-0.1,4.4,1.1,4.5,2.7C4.7,4.3,3.8,5.7,2.5,5.8C1.3,5.9,.2,4.7,.0,3.1z"
            />
            <path
              strokeWidth="0.4799999892711639"
              id="shape96"
              fill="#ff7878"
              fillRule="nonzero"
              transform="matrix(0.999391,-0.034899,0.034899,0.999391,43.872546,53.207583)"
              stroke="#ff7878"
              d="M.0,2.5C-0.1,1.2,.6,.1,1.6,.0C2.6,-0.1,3.5,.9,3.6,2.2C3.7,3.5,3.0,4.6,2.0,4.7C1.0,4.7,.1,3.8,.0,2.5z"
            />
            <path
              id="shape97"
              fill="#ffffff"
              fillRule="nonzero"
              transform="matrix(0.999391,-0.034899,0.034899,0.999391,43.863718,53.572802)"
              fillOpacity="0.12"
              d="M.0,2.1C-0.0,1.2,.3,.5,.8,.0C.8,.0,3.0,3.8,3.0,3.8C2.7,4.1,2.4,4.3,2.0,4.3C1.0,4.4,.1,3.4,.0,2.1z"
            />
            <path
              id="shape98"
              fill="#ffffff"
              fillRule="nonzero"
              transform="translate(20.295680,40.559950)"
              fillOpacity="0.29"
              d="M.0,.0L8.7,4.5L8.7,8.4L.0,3.7L.0,.0z"
            />
          </g>
          <path
            strokeWidth="1"
            id="shape99"
            fill="none"
            transform="translate(982.065102,444.955247)"
            stroke="#868686"
            d="M.0,.0L.0,211.6L6.3,211.6"
          />
          <g id="shape100" transform="translate(976.661273,655.931270)">
            <path strokeWidth="1" fill="none" stroke="#191919" d="" />
          </g>
          <path
            strokeWidth="1"
            id="shape101"
            fill="none"
            transform="translate(981.898386,663.931270)"
            stroke="#868686"
            d="M.0,.0L.0,-7.4L-6.0,-7.4"
          />
          <g id="group102" transform="translate(929.135598,640.506270)">
            <path
              strokeWidth="0.5"
              id="shape103"
              fill="#d6d8df"
              fillRule="nonzero"
              transform="translate(0.000000,12.017941)"
              stroke="#d6d8df"
              d="M.0,.0L24.6,13.1L24.6,18.8L.0,5.7L.0,.0z"
            />
            <path
              strokeWidth="1"
              id="shape104"
              fill="none"
              transform="translate(0.098093,15.159675)"
              stroke="#000000"
              strokeDasharray="3.8,1.2"
              d="M.0,.0L21.6,11.6"
            />
            <path
              strokeWidth="1"
              id="shape105"
              fill="#ffffff"
              fillRule="nonzero"
              transform="translate(9.503329,18.929344)"
              stroke="#ffffff"
              d="M.0,.0L4.8,2.6L4.8,5.2L.0,2.5L.0,.0z"
            />
            <path
              strokeWidth="0.5"
              id="shape106"
              fill="#caccd3"
              fillRule="nonzero"
              transform="matrix(-1.000000,0.000000,0.000000,1.000000,47.525676,13.084056)"
              stroke="#caccd3"
              d="M.0,.0L22.9,12.1L22.9,17.8L.0,5.7L.0,.0z"
            />
            <path
              strokeWidth="0.5"
              id="shape107"
              fill="#e2e4e9"
              fillRule="nonzero"
              stroke="#e2e4e9"
              d="M24.6,25.1L.0,12.0L22.9,.0L47.5,13.1L24.6,25.1z"
            />
            <path
              strokeWidth="1"
              id="shape108"
              fill="#62bc85"
              fillRule="nonzero"
              transform="translate(9.705023,19.342194)"
              stroke="#62bc85"
              d="M.0,.0L4.4,2.3L4.4,4.3L.0,2.0L.0,.0z"
            />
            <path
              strokeWidth="0.25"
              id="shape109"
              fill="#e85464"
              fillRule="nonzero"
              transform="matrix(0.999391,-0.034899,0.034899,0.999391,20.967731,25.314246)"
              stroke="#e85464"
              d="M.0,1.5C-0.1,.7,.4,.1,1.0,.0C1.6,-0.0,2.1,.5,2.2,1.3C2.3,2.1,1.8,2.8,1.2,2.8C.6,2.9,.1,2.3,.0,1.5z"
            />
            <path
              strokeWidth="0.4799999892711639"
              id="shape110"
              fill="#ff7878"
              fillRule="nonzero"
              transform="matrix(0.999391,-0.034899,0.034899,0.999391,21.199635,25.590756)"
              stroke="#ff7878"
              d="M.0,1.2C-0.0,.6,.3,.0,.8,.0C1.3,-0.0,1.7,.4,1.7,1.0C1.8,1.7,1.5,2.2,1.0,2.2C.5,2.3,.1,1.8,.0,1.2z"
            />
            <path
              id="shape111"
              fill="#ffffff"
              fillRule="nonzero"
              transform="matrix(0.999391,-0.034899,0.034899,0.999391,21.195379,25.766395)"
              fillOpacity="0.12"
              d="M.0,1.0C-0.0,.6,.1,.2,.4,.0C.4,.0,1.4,1.8,1.4,1.8C1.3,2.0,1.2,2.1,1.0,2.1C.5,2.1,.1,1.6,.0,1.0z"
            />
            <path
              id="shape112"
              fill="#ffffff"
              fillRule="nonzero"
              transform="translate(9.806956,19.507581)"
              fillOpacity="0.29"
              d="M.0,.0L4.2,2.2L4.2,4.0L.0,1.8L.0,.0z"
            />
          </g>
          <g id="shape113" transform="translate(606.000000,139.000000)">
            <path
              strokeWidth="1.333333373069763"
              fill="#ebebeb"
              fillRule="nonzero"
              stroke="#000000"
              d="M.0,.0L113.0,.0L113.0,82.0L.0,82.0L.0,.0z"
            />
            <text style={getStyle("#191919", "14pt")} xmlSpace="preserve">
              <tspan x="10.9" y="26.0">
                Hardware
              </tspan>
              <tspan x="10.9" y="48.0">
                Security
              </tspan>
              <tspan x="16.6" y="70.0">
                Module*
              </tspan>
            </text>
          </g>
          <g
            id="shape114"
            transform="matrix(0.000000,1.000000,-1.000000,0.000000,804.875000,75.000000)"
          >
            <text style={getStyle("#868686", "12pt")} xmlSpace="preserve">
              <tspan x="4.0" y="18.9">
                * to be added in future implementation
              </tspan>
            </text>
          </g>
          <path
            strokeWidth="1"
            id="shape115"
            fill="none"
            transform="translate(230.873472,633.000000)"
            stroke="#191919"
            d="M.0,.0L-0.0,85.0"
          />

          <g id="shape117" transform="translate(889.000000,699.281270)">
            <text style={getStyle("#191919", "18pt")} xmlSpace="preserve">
              <tspan x="59.8" y="25.9">
                Other
              </tspan>
              <tspan x="9.5" y="52.9">
                cluster units
              </tspan>
            </text>
          </g>

          <g id="shape119" transform="translate(662.785023,283.709375)">
            <path
              strokeWidth="1"
              fill="none"
              stroke="#191919"
              d="M-0.0,-6.0L-0.3,-56.7"
            />
            <path
              strokeWidth="1"
              fill="#191919"
              stroke="#191919"
              d="M3.0,-6.0L.0,.0L-3.0,-6.0L3.0,-6.0"
              strokeLinecap="round"
            />
            <path
              strokeWidth="1"
              fill="#191919"
              stroke="#191919"
              d="M-3.3,-56.7L-0.3,-62.7L2.7,-56.7L-3.3,-56.7"
              strokeLinecap="round"
            />
          </g>
          <HoverableElement
            elementIds={[
              io,
              "Ethernet Port",
              "Ethernet Port 2",
              "Ethernet Cord Ends",
              "W5500 Parent",
            ]}
          >
            {/* <g
              className={`hoverable ${
                highlightedParts.includes(io) ? "highlighted" : ""
              }`}
              onMouseEnter={() => handleHover(io)}
              onMouseLeave={() => handleHover(null)}
              onClick={() => handleClick(io)}
            > */}
            <g id="shape120" transform="translate(233.054414,262.000000)">
              <path
                strokeWidth="1"
                fill="none"
                stroke="#191919"
                d="M.0,-6.0L.0,-218.0"
              />
              <path
                strokeWidth="1"
                fill="#191919"
                stroke="#191919"
                d="M3.0,-6.0L.0,.0L-3.0,-6.0L3.0,-6.0"
                strokeLinecap="round"
              />
              <path
                strokeWidth="1"
                fill="#191919"
                stroke="#191919"
                d="M-3.0,-218.0L.0,-224.0L3.0,-218.0L-3.0,-218.0"
                strokeLinecap="round"
              />
            </g>
            <g id="shape122" transform="translate(602.000000,182.000000)">
              <path
                strokeWidth="1"
                fill="none"
                stroke="#191919"
                d="M-6.0,.0L-368.9,.0"
              />
              <path
                strokeWidth="1"
                fill="#191919"
                stroke="#191919"
                d="M-6.0,-3.0L.0,.0L-6.0,3.0L-6.0,-3.0"
                strokeLinecap="round"
              />
            </g>
            <g id="shape123" transform="translate(253.000000,185.904687)">
              <text style={getStyle("#191919", "14pt")} xmlSpace="preserve">
                <tspan x="4.0" y="18.1">
                  Data I/O
                </tspan>
              </text>
            </g>
            <g
              id="shape84"
              transform="matrix(0.000000,-1.000000,1.000000,0.000000,174.000000,243.000000)"
            >
              <path
                fill="#ffffff"
                fillRule="nonzero"
                d="M.0,.0L152.0,.0L152.0,55.7L.0,55.7L.0,.0z"
              />
              <text style={getStyle("#191919", "14pt")} xmlSpace="preserve">
                <tspan x="4.0" y="23.9">
                  Management &amp;
                </tspan>
                <tspan x="4.0" y="45.9">
                  data loading
                </tspan>
              </text>
            </g>
            <g id="shape87" transform="translate(665.000000,75.000000)">
              <text style={getStyle("#191919", "14pt")} xmlSpace="preserve">
                <tspan x="4.0" y="23.0">
                  Encrypted
                </tspan>
                <tspan x="4.0" y="45.0">
                  I/O
                </tspan>
              </text>
            </g>
          </HoverableElement>

          <path
            strokeWidth="1"
            id="shape125"
            fill="none"
            transform="translate(662.785023,628.238498)"
            stroke="#191919"
            d="M.0,.0L-0.0,-247.2"
          />
          <path
            strokeWidth="1"
            id="shape126"
            fill="none"
            transform="translate(532.445046,669.545600)"
            stroke="#191919"
            d="M.0,.0L34.6,-0.4"
          />
          <path
            strokeWidth="1"
            id="shape127"
            fill="none"
            transform="translate(395.445046,669.545600)"
            stroke="#191919"
            d="M.0,.0L-164.1,.0"
          />
          <path
            strokeWidth="1"
            id="shape128"
            fill="none"
            transform="translate(662.785023,380.109375)"
            stroke="#191919"
            d="M.0,.0L.0,198.6L-61.8,198.6C-61.8,195.3,-64.5,192.6,-67.8,192.6C-71.2,192.6,-73.8,195.3,-73.8,198.6L-160.2,198.6L-160.2,260.0"
          />
          <path
            strokeWidth="1"
            id="shape129"
            fill="none"
            transform="translate(614.127300,513.102900)"
            stroke="#c00000"
            d="M.0,.0L.0,-17.1"
          />
          <path
            strokeWidth="1"
            id="shape130"
            fill="none"
            transform="translate(543.038843,441.000000)"
            stroke="#c00000"
            d="M.0,.0L.1,-17.0"
          />
          <path
            strokeWidth="1"
            id="shape131"
            fill="none"
            transform="translate(594.944053,628.238498)"
            stroke="#191919"
            d="M.0,.0L.0,-132.2"
          />
          <path
            strokeWidth="1"
            id="shape132"
            fill="none"
            transform="translate(404.445046,342.385969)"
            stroke="#191919"
            d="M.0,.0L-28.0,.0L-28.0,96.8L-79.0,96.8"
          />
          <path
            strokeWidth="1"
            id="shape133"
            fill="none"
            transform="translate(404.445046,326.871694)"
            stroke="#191919"
            d="M.0,.0L-37.4,-0.0"
          />
          <path
            strokeWidth="1"
            id="shape134"
            fill="none"
            transform="translate(541.445046,331.909375)"
            stroke="#191919"
            d="M.0,.0L25.6,.0"
          />
          <g id="shape135" transform="translate(662.500000,139.000000)">
            <path
              strokeWidth="1"
              fill="none"
              stroke="#191919"
              d="M.0,-6.0L-0.0,-95.0"
            />
            <path
              strokeWidth="1"
              fill="#191919"
              stroke="#191919"
              d="M3.0,-6.0L.0,.0L-3.0,-6.0L3.0,-6.0"
              strokeLinecap="round"
            />
            <path
              strokeWidth="1"
              fill="#191919"
              stroke="#191919"
              d="M-3.0,-95.0L-0.0,-101.0L3.0,-95.0L-3.0,-95.0"
              strokeLinecap="round"
            />
          </g>
        </svg>
      </div>
    </div>
  );
}

export function Diagram({
  highlightedParts,
  setHighlightedParts,
  switchCamera,
  isDieCoverOpen,
  toggleDieCover,
}: {
  highlightedParts: string[];
  setHighlightedParts: Dispatch<SetStateAction<string[]>>;
  switchCamera: any; // TODO
  isDieCoverOpen: any; // TODO
  toggleDieCover: any; // TODO
}): JSX.Element {
  return (
    <DiagramProvider
      highlightedParts={highlightedParts}
      setHighlightedParts={setHighlightedParts}
      switchCamera={switchCamera}
      isDieCoverOpen={isDieCoverOpen}
      toggleDieCover={toggleDieCover}
    >
      <DiagramContent />
    </DiagramProvider>
  );
}
